import type { Feature, FeatureCollection, Position } from 'geojson';
import type { Map } from 'mapbox-gl';

import { bbox, buffer } from '@turf/turf';

import { BUFFER_METERS, DEFAULT_PADDING, GEOJSON_PROPERTY } from './constants';
import type { MapPadding } from './types';

const useMapbox = () => {
    const getMapboxFeaturesUnderPoint = (map?: Map, point?: Position) => {
        if (!map || !point) {
            return [];
        }

        const mapPosition = map.project({
            lat: point[1],
            lng: point[0]
        });

        return map.queryRenderedFeatures(mapPosition).map(({ geometry, id, properties, type }) => ({
            geometry,
            id: id ?? properties?.[GEOJSON_PROPERTY.ID],
            properties,
            type
        }));
    };

    const fitBoundsToFeatures = (
        map?: Map,
        features: Feature[] = [],
        padding: MapPadding = DEFAULT_PADDING
    ) => {
        if (!map) {
            return;
        }

        const bufferedFeatures = features.map(feature => {
            const buffered = buffer(feature, BUFFER_METERS, { units: 'meters' });

            return buffered ?? feature;
        });

        const featureCollection = {
            features: bufferedFeatures,
            type: 'FeatureCollection'
        } as FeatureCollection;

        const [minLng, minLat, maxLng, maxLat] = bbox(featureCollection);

        const bounds = [
            [minLng, minLat],
            [maxLng, maxLat]
        ] as [[number, number], [number, number]];

        map.fitBounds(bounds, {
            linear: true,
            padding
        });
    };

    const fitToBoundsToFeature = (map: Map, feature: Feature) => {
        fitBoundsToFeatures(map, [feature]);
    };

    return {
        fitBoundsToFeatures,
        fitToBoundsToFeature,
        getMapboxFeaturesUnderPoint
    };
};

export default useMapbox;
